import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { formatDate } from '@angular/common';
import * as FileSaver from 'file-saver';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { SelectionAggregator } from '../../../../shared/utils/selection-aggregator';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';

import { LoadGrants, LoadGrantsFailure, LoadGrantsSuccess, GrantActionTypes } from '../../store/actions/grant.actions';
import { SummaryGrantModel } from '../../models/summary-grant.model';
import { GrantService } from '../../services/grant.service';
import { createGrantTableConfig, TableRowAction } from './grant-table.config';

import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { SubventionService } from '../../../../../modules/invoice/subvention/subvention.service';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';

@Component({
  standalone: false,
  selector: 'app-grant-list',
  templateUrl: './grant-list.components.html',
  styleUrls: ['./grant-list.components.css']
})
export class GrantListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('invoiceSummary') invoiceSummary!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Subvenciones';
  subTitle = 'Consulta, edita o gestiona tus subvenciones';

  summary: any = null;
  selectedIds: string[] = [];
  selectionTotals = new SelectionAggregator({
    total: (r: any) => r.totalWithoutTax,
  });

  monthsControl: FormControl = new FormControl([]);
  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private destroy$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private grantService: GrantService,
    private subventionService: SubventionService,
    private addFileService: AddFileService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewSummaryGrantFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored);
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) { defaultFilter = {}; }
    }

    this.tableConfig = createGrantTableConfig(
      {
        onCreate: () => this.create(),
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.DownloadImage]: (row) => this.downloadFileImage(row),
          [TableRowAction.DownloadPdf]: (row) => this.downloadFilePdf(row),
        },
        selectable: true,
        summaryTemplate: this.invoiceSummary,
        columns: [
          { key: 'subventionType', label: 'Tipo', sortable: false },
          { key: 'provider', label: 'Proveedor', sortable: true, sortKey: 'Provider', onCellClick: (row) => this.edit(row) },
          { key: 'elementDescription', label: 'Elemento', sortable: true, sortKey: 'ElementDescription' },
          { key: 'totalWithoutTax', label: 'Total recibido', sortable: true, sortKey: 'TotalWithoutTax', formatFn: (val) => Utils.FormatMoney(val) + ' €' },
          { key: 'date', label: 'Fecha', sortable: true, sortKey: 'Date', formatFn: (val) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '' },
          {
            key: 'paided', label: 'Fecha de cobro', sortable: true, sortKey: 'Paided',
            badge: {
              variantFn: (value: string) => value ? 'success' : 'warning',
              labelFn: (value: string) => value ? formatDate(value, 'dd/MM/yyyy', 'es-ES') : 'Pendiente',
            },
          },
          { key: 'paymentMethodInfo', label: 'Cuenta bancaria', sortable: false },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Excel amortización', icon: 'file_download', type: 'button', callback: () => this.exportAmortisation() },
        ],
        rowConfig: {
          onToggleSelect: (row: any, selected: boolean) => {
            const id = row.uid;
            if (selected) {
              this.addSelected(id);
              this.selectionTotals.add(row);
            } else {
              this.removeSelected(id);
              this.selectionTotals.remove(row);
            }
          }
        },
      },
      {
        filterView: 'viewSummaryGrantFilters',
        defaultFilter,
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };

    this.subs.push(
      this.actions$
        .pipe(
          ofType<LoadGrantsSuccess>(GrantActionTypes.LoadGrantsSuccess),
          map(action => action.payload)
        )
        .subscribe(res => {
          const expanded = new Set((this.t1.tableState.snapshot.data || []).filter((r: any) => r.__expanded).map((r: any) => r.uid));
          const items = (res.items || []).map(item => ({
            ...item,
            __selected: this.selectedIds.indexOf(item.uid) !== -1,
            __expanded: expanded.has(item.uid),
          }));
          this.summary = res.summary;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(res.total);
          this.t1.tableState.setLoading(false);
        })
    );

    this.subs.push(
      this.actions$
        .pipe(ofType<LoadGrantsFailure>(GrantActionTypes.LoadGrantsFailure))
        .subscribe(() => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar las subvenciones');
        })
    );
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.invoiceSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.monthsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        const raw = Array.isArray(value) ? value : [];
        const clean = raw.filter((v: any) => typeof v === 'number');
        if (clean.length !== raw.length) {
          this.monthsControl.setValue(clean, { emitEvent: false });
        }
        this.t1.tableState.patchFilters({ months: clean });
      });

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(s => ((s.filters && (s.filters as any).months) || []) as number[]),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this.destroy$),
      )
      .subscribe(months => this.monthsControl.setValue(months, { emitEvent: false }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewSummaryGrantFilters', JSON.stringify(state));
        this.dispatchLoad();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  clearSelection(): void {
    this.selectedIds = [];
    this.selectionTotals.reset();
    const data = (this.t1.tableState.snapshot.data || []).map((row: any) => ({ ...row, __selected: false }));
    this.t1.tableState.setData(data);
  }

  create(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.router.navigate(['/invoice/subvention/create']);
  }

  edit(row: SummaryGrantModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.router.navigate(['/invoice/subvention/edit/' + row.uid]);
  }

  delete(row: SummaryGrantModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar subvención',
      subtitle: row.elementDescription || row.provider || undefined,
      message: '¿Seguro que quieres eliminar esta subvención? También se eliminará su asiento contable y, si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.grantDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando subvención…',
      errorText: 'No se pudo eliminar la subvención',
      action: () => this.subventionService.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Subvención eliminada correctamente');
      this.removeSelectedRow(row);
      this.dispatchLoad();
    });
  }

  downloadFileImage(row: SummaryGrantModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.addFileService.downloadFileImage(row.uid, consts.SUBVENTION_ENTITY, consts.EMPTY_GUID))
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetFileName('Subvencion', consts.EXT_PNG));
        this.notify.ok('Documento descargado correctamente');
      })
      .catch(() => this.notify.error('No se pudo descargar la imagen'));
  }

  downloadFilePdf(row: SummaryGrantModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.addFileService.downloadFilePDF(row.uid, consts.SUBVENTION_ENTITY, consts.EMPTY_GUID))
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetPdfName('Subvencion'));
        this.notify.ok('Documento descargado correctamente');
      })
      .catch(() => this.notify.error('No se pudo descargar el PDF'));
  }

  exportAmortisation(): void {
    if (!this.tier.requireDocumentsTier()) return;

    const fromSelection = this.selectedIds.length > 0;
    let exported = 0;

    const task = (fromSelection ? Promise.resolve([...this.selectedIds]) : this.loadFilteredUids())
      .then(uids => {
        exported = uids.length;
        if (exported === 0) throw { empty: true };
        return this.subventionService.getBookAmortisation(uids, consts.EMPTY_GUID);
      });

    const scope = fromSelection ? 'de la selección' : 'del listado';
    this.loading.run(`Generando Excel de amortización ${scope}…`, task)
      .then((blob: Blob) => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Amortizacion_Subvenciones'));
        this.notify.ok(`Excel generado con ${exported} ${exported === 1 ? 'subvención' : 'subvenciones'}`);
      })
      .catch(error => {
        if (error && error.empty) {
          this.notify.warning('No hay subvenciones que exportar con los filtros actuales');
          return;
        }
        this.notify.error('No se pudo generar el Excel de amortización');
      });
  }

  private grantDetails(row: SummaryGrantModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.subventionType) details.push({ label: 'Tipo', value: row.subventionType });
    if (row.provider) details.push({ label: 'Proveedor', value: row.provider });
    if (row.date) details.push({ label: 'Fecha', value: formatDate(row.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Total recibido', value: Utils.FormatMoney(row.totalWithoutTax) + ' €' });
    return details;
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private dispatchLoad(): void {
    this.t1.tableState.setLoading(true);
    this.store.dispatch(new LoadGrants({ filter: buildSearchPayload(this.lastState) }));
  }

  private loadFilteredUids(): Promise<string[]> {
    return firstValueFrom(this.grantService.getBySearch({ ...buildSearchPayload(this.lastState), page: 1, pageSize: 0 }))
      .then((res: any) => {
        if (!res || !res.isSuccess) throw new Error();
        return (res.values || []).map((item: any) => item.uid);
      });
  }

  private removeSelectedRow(row: SummaryGrantModel): void {
    if (this.selectedIds.indexOf(row.uid) === -1) return;
    this.removeSelected(row.uid);
    this.selectionTotals.remove(row);
  }

  private addSelected(id: string): void {
    if (this.selectedIds.indexOf(id) === -1) this.selectedIds.push(id);
  }

  private removeSelected(id: string): void {
    const idx = this.selectedIds.indexOf(id);
    if (idx !== -1) this.selectedIds.splice(idx, 1);
  }
}
